import {
  CreateSettingsProfileResponse,
  DeleteSettingsProfileResponse,
  FetchProfileListResponse,
  SettingsProfileType,
  UpdateSettingsProfileResponse,
  fetchProfileListPayload,
} from '../models';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import { createAction } from '@reduxjs/toolkit';

interface FETCH_PROFILE_LIST_VARIABLES {
  schemaId: string;
  limit: number;
  offset: number;
}
export const FETCH_PROFILE_LIST_REQUEST = createAction<fetchProfileListPayload>(
  'SETTINGS_PROFILE/FETCH_PROFILE_LIST_REQUEST'
);
export const FETCH_PROFILE_LIST_SUCCESS = createGraphQLSuccessAction<
  FetchProfileListResponse,
  FETCH_PROFILE_LIST_VARIABLES,
  true
>('SETTINGS_PROFILE/FETCH_PROFILE_LIST_SUCCESS');

export const FETCH_PROFILE_LIST_FAILURE = createGraphQLFailureAction(
  'SETTINGS_PROFILE/FETCH_PROFILE_LIST_FAILURE'
);

export const CREATE_SETTINGS_PROFILE_REQUEST = createAction<{
  data: SettingsProfileType['data'];
}>('SETTINGS_PROFILE/CREATE_SETTINGS_PROFILE_REQUEST');
export const CREATE_SETTINGS_PROFILE_SUCCESS =
  createGraphQLSuccessAction<CreateSettingsProfileResponse>(
    'SETTINGS_PROFILE/CREATE_SETTINGS_PROFILE_SUCCESS'
  );
export const CREATE_SETTINGS_PROFILE_FAILURE = createGraphQLFailureAction(
  'SETTINGS_PROFILE/CREATE_SETTINGS_PROFILE_FAILURE'
);

export const UPDATE_SETTINGS_PROFILE_REQUEST = createAction<{
  profile: SettingsProfileType;
}>('SETTINGS_PROFILE/UPDATE_SETTINGS_PROFILE_REQUEST');
export const UPDATE_SETTINGS_PROFILE_SUCCESS =
  createGraphQLSuccessAction<UpdateSettingsProfileResponse>(
    'SETTINGS_PROFILE/UPDATE_SETTINGS_PROFILE_SUCCESS'
  );
export const UPDATE_SETTINGS_PROFILE_FAILURE = createGraphQLFailureAction(
  'SETTINGS_PROFILE/UPDATE_SETTINGS_PROFILE_FAILURE'
);

/**
 * The SOFT delete: an update that raises `isDeleted`, not a removal. The row
 * leaves the table and every picker but stays readable, so a file processed
 * with this profile can still name it (VE-28768).
 */
export const DELETE_SETTINGS_PROFILE_REQUEST = createAction<{
  profileId: string;
}>('SETTINGS_PROFILE/DELETE_SETTINGS_PROFILE_REQUEST');
export const DELETE_SETTINGS_PROFILE_SUCCESS =
  createGraphQLSuccessAction<DeleteSettingsProfileResponse>(
    'SETTINGS_PROFILE/DELETE_SETTINGS_PROFILE_SUCCESS'
  );
export const DELETE_SETTINGS_PROFILE_FAILURE = createGraphQLFailureAction(
  'SETTINGS_PROFILE/DELETE_SETTINGS_PROFILE_FAILURE'
);

export const actionFetchProfileList = (payload: fetchProfileListPayload) =>
  FETCH_PROFILE_LIST_REQUEST(payload);

export const actionCreateSettingsProfile = (payload: {
  data: SettingsProfileType['data'];
}) => CREATE_SETTINGS_PROFILE_REQUEST(payload);

export const actionUpdateSettingsProfile = (payload: {
  profile: SettingsProfileType;
}) => UPDATE_SETTINGS_PROFILE_REQUEST(payload);

export const actionDeleteSettingsProfile = (payload: { profileId: string }) =>
  DELETE_SETTINGS_PROFILE_REQUEST(payload);

/**
 * Terminal actions for the flag writes.
 *
 * Raising `isDeleted` reuses the update MUTATION, but must not reuse the
 * update RESULT: a failed soft delete reported through
 * UPDATE_SETTINGS_PROFILE_FAILURE reads "error while updating settings
 * profile", and a successful one said nothing at all. Mirrors
 * SET_REDACTION_CODE_DELETED_* on the codes module.
 */
export const SET_SETTINGS_PROFILE_DELETED_SUCCESS =
  createGraphQLSuccessAction<UpdateSettingsProfileResponse>(
    'SETTINGS_PROFILE/SET_SETTINGS_PROFILE_DELETED_SUCCESS'
  );
export const SET_SETTINGS_PROFILE_DELETED_FAILURE = createGraphQLFailureAction(
  'SETTINGS_PROFILE/SET_SETTINGS_PROFILE_DELETED_FAILURE'
);

export const RESTORE_SETTINGS_PROFILE_REQUEST = createAction<{
  profileId: string;
}>('SETTINGS_PROFILE/RESTORE_SETTINGS_PROFILE_REQUEST');

/**
 * The HARD delete, offered only in the deleted view behind its own
 * confirmation. Separate from DELETE_SETTINGS_PROFILE_REQUEST, which is the
 * soft one — after this the record is gone and nothing can name it.
 */
export const PERMANENTLY_DELETE_SETTINGS_PROFILE_REQUEST = createAction<{
  profileId: string;
}>('SETTINGS_PROFILE/PERMANENTLY_DELETE_SETTINGS_PROFILE_REQUEST');

export const actionRestoreSettingsProfile = (payload: { profileId: string }) =>
  RESTORE_SETTINGS_PROFILE_REQUEST(payload);

export const actionPermanentlyDeleteSettingsProfile = (payload: {
  profileId: string;
}) => PERMANENTLY_DELETE_SETTINGS_PROFILE_REQUEST(payload);
